import { useState, useEffect } from "react";
import { apiService } from "../../api/apiAdapter";
import { useNavigate } from "react-router-dom";
import { usePromotion } from "../../hooks/usePromotion";
import styles from "./purchase.module.scss";
import SelectedIcon from "../../assets/icons/Selected.svg";
import UnSelectedIcon from "../../assets/icons/Unselected.svg";
import GuidelineIcon from "../../assets/icons/Guideline.svg";
import ObesiDIcon from "../../assets/icons/ObesiD.svg";
import DRTCLogoIcon from "../../assets/icons/DRTCLogo.svg";
import AIDHLogoIcon from "../../assets/icons/AIDHLogo.svg";

const subscriptions = {
  tg: "Therapeutic Guidelines",
  drtc: "Don't Rush to Crush",
  obesid: "ObesiD",
  aidh: "AIDH",
};

const basePriceMap = {
  tg: parseFloat(import.meta.env.VITE_TG_SINGLE_USER_PRICE || 0),
  obesid: parseFloat(import.meta.env.VITE_OB_SINGLE_USER_PRICE || 0),
  drtc: parseFloat(import.meta.env.VITE_DRTC_SINGLE_USER_PRICE || 0),
  aidh: parseFloat(import.meta.env.VITE_AIDH_SINGLE_USER_PRICE || 0),
};

const Purchase = ({ initialSelections, hasAppliedCoupon }) => {
  const navigate = useNavigate();

  const initialApps = initialSelections?.selectedApps || [];
  const visibleApps =
    initialSelections?.availableApps?.length > 0
      ? initialSelections.availableApps
      : Object.keys(subscriptions);
  const [purchaseType, setPurchaseType] = useState(
    initialSelections?.purchaseOption || "singleUser",
  );

  const [selectedApps, setSelectedApps] = useState(initialApps);

  const promoTG = usePromotion("151");
  const promoObesID = usePromotion("2151");
  const promoDRTC = usePromotion("3151");
  const promoAIDH = usePromotion("4151");

  const promoByApp = { tg: promoTG, obesid: promoObesID, drtc: promoDRTC, aidh: promoAIDH };

  useEffect(() => {
    if (initialSelections?.selectedApps) {
      setSelectedApps(initialSelections.selectedApps);
      setPurchaseType(initialSelections.purchaseOption);
    }
  }, [initialSelections]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialSelections) {
      setSelectedApps(initialSelections.selectedApps || []);
      setPurchaseType(initialSelections.purchaseOption);
    }
  }, [initialSelections]);

  function generateToken(length = 32) {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let token = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      token += characters[randomIndex];
    }
    return token;
  }

  const getAppPrice = (app) => {
    const promo = promoByApp[app];
    return promo ? parseFloat(promo.price) : basePriceMap[app];
  };

  const calculateTotal = () => {
    return selectedApps.reduce((total, app) => total + getAppPrice(app), 0).toFixed(2);
  };

  const calculateOriginalTotal = () => {
    return selectedApps.reduce((total, app) => total + (basePriceMap[app] || 0), 0).toFixed(2);
  };

  const hasActivePromo = selectedApps.some((app) => promoByApp[app]);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const currentToken = generateToken();
    localStorage.setItem("tgCartToken", currentToken);

    try {
      const existingCartResponse = await apiService.getCart();
      const existingItems = Array.isArray(existingCartResponse) ? existingCartResponse : [];
      for (const appKey of selectedApps) {
        let subscriptionid;
        if (appKey === "tg") {
          subscriptionid = purchaseType === "singleUser" ? "151" : "170";
        } else if (appKey === "obesid") {
          subscriptionid = purchaseType === "singleUser" ? "2151" : "2170";
        } else if (appKey === "drtc") {
          subscriptionid = purchaseType === "singleUser" ? "3151" : "3170";
        } else if (appKey === "aidh") {
          subscriptionid = purchaseType === "singleUser" ? "4151" : "4170";
        }

        // Check if it's already in the cart
        const isDuplicate = existingItems.some((item) => item.subscriptionid === subscriptionid);

        if (isDuplicate) {
          const subscriptionName = subscriptions[appKey] || `Item ${subscriptionid}`;
          alert(`${subscriptionName} is already in cart, skipping...`);
          continue;
        }

        // Only Add if it's not a duplicate
        const newItem = {
          token: currentToken,
          subscriptionid: subscriptionid,
          ordertype: "New",
          individualprice: getAppPrice(appKey),
          quantity: 1,
          hasappliedcoupon: hasAppliedCoupon || false,
        };

        try {
          const response = await apiService.addToCart(newItem);
          if (!response.success) {
            console.warn(`Could not add ${appKey}: ${response.message}`);
          }
        } catch (err) {
          console.error(`API Error for ${appKey}:`, err);
        }
      }

      // Always navigate to cart at the end
      navigate("/myCart");
    } catch (error) {
      console.error("Cart processing error:", error);
      alert("There was an issue updating your cart. Redirecting to cart page.");
      navigate("/myCart");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleApp = (appId) => {
    setSelectedApps(
      (prev) =>
        prev.includes(appId)
          ? prev.filter((id) => id !== appId) // Remove if exists
          : [...prev, appId], // Add if missing
    );
  };

  const renderPurchaseContent = () => {
    if (purchaseType === "singleUser") {
      return (
        <div className={styles.purchaseDetails}>
          <hr className={styles.line} />
          <div className={styles.selectedSubscriptionTitle}>Selected Subscription</div>
          {selectedApps.map((app) => (
            <div key={app} className={styles.productItem}>
              <p className={styles.productTitle}>
                <i>{subscriptions[app] || app}</i> 12 month subscription
              </p>
              <p className={styles.subDetail}>
                Includes access to the <i>{subscriptions[app] || app}</i> app
              </p>
              {promoByApp[app] ? (
                <div>
                  <span className={styles.originalPrice}>${basePriceMap[app].toFixed(2)}</span>
                  <span>
                    {" "}
                    {promoByApp[app].label} price
                    {promoByApp[app].footnote ? "*" : ""} $
                    {parseFloat(promoByApp[app].price).toFixed(2)}
                  </span>
                </div>
              ) : (
                <p>${basePriceMap[app].toFixed(2)}</p>
              )}
              <div style={{ marginBottom: "1rem" }}></div>
            </div>
          ))}

          <hr className={styles.line} />
          <div className={styles.priceRow}>
            <span>Total cost:</span>
            <div>
              {hasActivePromo && (
                <span className={styles.originalPrice}>${calculateOriginalTotal()}</span>
              )}
              <span className={styles.totalPrice}>${calculateTotal()}</span>
            </div>
          </div>
          {hasActivePromo && (
            <p className={styles.promoFootnote}>
              {[
                ...new Set(
                  selectedApps
                    .filter((app) => promoByApp[app]?.footnote)
                    .map((app) => promoByApp[app].footnote),
                ),
              ].join(" ")}
            </p>
          )}
          <button
            onClick={handleAddToCart}
            className={styles.mainActionBtn}
            disabled={isSubmitting || selectedApps.length === 0}
          >
            {isSubmitting ? "Adding to cart..." : "Add to cart"}
          </button>
        </div>
      );
    }

    return null; // Institution form removed from here
  };
  const PRODUCTS = [
    { id: "tg", name: "Therapeutic Guidelines", logo: GuidelineIcon },
    { id: "obesid", name: "ObesiD", logo: ObesiDIcon },
    { id: "drtc", name: "DRTC", logo: DRTCLogoIcon },
    { id: "aidh", name: "AIDH", logo: AIDHLogoIcon },
  ];
  return (
    <>
      <div className={styles.purchaseWrapper}>
        <div className={styles.purchaseBox}>
          <div className={styles.purchaseHeader}>
            <div>Purchase</div>
            <div className={styles.selecedCount}>{selectedApps.length} selected</div>
          </div>

          <div className={styles.logoSection}>
            {visibleApps.map((app) => {
              const product = PRODUCTS.find((item) => item.id === app);
              const isSelected = selectedApps.includes(app);

              if (!product) {
                return null;
              }

              return (
                <div
                  className={`${styles.pubContainer} ${isSelected ? styles.selectedPub : ""}`}
                  key={product.id}
                  onClick={() => handleToggleApp(product.id)}
                  style={{ cursor: "pointer", position: "relative" }}
                >
                  <div className={styles.selectionIndicator}>
                    {isSelected ? (
                      <img src={SelectedIcon} alt="selected" />
                    ) : (
                      <img src={UnSelectedIcon} alt="unSelected" />
                    )}
                  </div>
                  <img alt={product.id} src={product.logo} className={styles.appLogo} />
                </div>
              );
            })}
          </div>

          <div className={styles.contentContainer}>{renderPurchaseContent()}</div>

          <p className={styles.footerNote}>
            To speak to one of our friendly customer service team about your subscription options
            email <a href="mailto:sales@tg.org.au">sales@tg.org.au</a> or call +61 3 9329 1566.
          </p>
        </div>
      </div>
    </>
  );
};

export default Purchase;
